// Activation behavior for mentions and meme links inside read-only answer
// content. Shared by the live TipTap editor plugins and StaticAnswerBody's
// delegated click handler so both render paths behave identically.
import { isMobile } from 'lib/device'
import useStore from 'state/knovStore'
import {
    getCenterIndex,
    getSwiperRef,
    slideTo as slideToPanel,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'

type OpenRight = (filter: Record<string, unknown>) => void
type HighlightSearchHeader = (panelId: string) => void

export { MEME_LINK_PREFIX } from './memeLinkMarks'

export function activateMentionFromReadOnlyContent(
    mentionId: string,
    element: HTMLElement,
    openRight: OpenRight,
    highlightSearchHeader: HighlightSearchHeader,
) {
    // Find the nearest element that carries panel context
    const panelContainer = element.closest('[data-panel-id]') as HTMLElement
    const panelId = panelContainer?.dataset?.panelId
    const editMode = panelContainer?.dataset?.editMode === 'true'

    // Don't open panel if in edit mode
    if (editMode) return
    if (!mentionId) return

    // Prefer dedupe if we can resolve an anchor panel
    if (panelId) {
        const panels = useStore.getState().panels
        const panelToRight = panels.getPanelToRight(panelId)

        if (
            panelToRight &&
            panelToRight.filter?.user === mentionId &&
            !panelToRight.filter?.questId
        ) {
            // Panel already shows this user's stream
            const swiper = getSwiperRef().current
            const currentIndex = panels.getIndexById(panelId)
            const needsSlide = isMobile || currentIndex > getCenterIndex()

            if (needsSlide && swiper) {
                swiper.once('slideChangeTransitionEnd', function afterSlide() {
                    panelToRight.shake()
                    highlightSearchHeader(panelToRight.panelId)
                })
                const numPanels = useStore.getState().numPanels
                if (numPanels === 1 && !isMobile) {
                    slideToPanel(getCenterIndex() + 1, SWIPE_SPEED_SLOW)
                } else {
                    swiper.slideNext(SWIPE_SPEED_FAST)
                }
            } else {
                panelToRight.shake()
                highlightSearchHeader(panelToRight.panelId)
            }
            return
        }
    }

    // Fallback: use the orchestrator hook to decide anchor panel
    openRight({ user: mentionId })
}

export function activateMemeFromReadOnlyContent(
    memeContent: string,
    element: HTMLElement | null,
    openRight: OpenRight,
) {
    // Guard for edit mode via the panel context, mirroring the editor plugin
    const panelContainer = element?.closest('[data-panel-id]') as HTMLElement | null
    if (panelContainer?.dataset?.editMode === 'true') return

    openRight({ all: true, meme: memeContent })
}
